// ═══════════════════════════════════════════════════════════════════════════════
// Marketing / entry home (public; shows auth shortcuts when logged in)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { APP_NAME, USER_ROLE } from '@fdh/shared';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { NotificationBell } from '../../components/NotificationBell';
import { MainMenuButton } from '../../components/MainMenuButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { appButtonClassName } from '../../components/ui/AppButton';
import { AuthLandingNavButtons } from '../../components/auth/AuthLandingNavButtons';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPostAuthHomePath } from '../../lib/authRedirect';
import { startGoogleRedirectAuth } from '../../lib/googleIdentity';
import { APP_ICONS } from '../../lib/iconAssets';
import { PLAY_BANNER_ABSOLUTE_TOP_4_CLASS, PLAY_BANNER_OFFSET_PADDING_CLASS } from '../../lib/layoutChrome';

const LANDING_REDIRECT_DELAY_MS = 2000;
const FEATURES_INTRO_URL = 'https://hfsworld.com/maid-easy/';
const LANDING_LEGAL_LINK_CLASS =
  'underline-offset-2 hover:text-gray-600 hover:underline dark:hover:text-gray-200';

export const HomePage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [isPageVisible, setIsPageVisible] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const handleOpenLogoutConfirm = () => {
    setLogoutConfirmOpen(true);
  };

  const handleCloseLogoutConfirm = () => {
    setLogoutConfirmOpen(false);
  };

  const handleConfirmLogout = async () => {
    await logout();
    setLogoutConfirmOpen(false);
    window.location.href = '/';
  };
  const handleGoogleLogin = useCallback(() => {
    setGoogleError('');
    setGoogleBusy(true);
    try {
      startGoogleRedirectAuth({
        intent: 'login',
        fromPath: '/',
      });
    } catch (error) {
      setGoogleBusy(false);
      setGoogleError(error instanceof Error ? error.message : t('auth.errorGeneric'));
    }
  }, [t]);

  useEffect(() => {
    const handlePageShow = () => {
      setGoogleBusy(false);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      setGoogleBusy(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setIsPageVisible(true);
  }, []);

  const isAwaitingAuthRedirect = !isLoading && isAuthenticated && user !== null;

  useEffect(() => {
    if (!isAwaitingAuthRedirect || !user) {
      return;
    }
    const targetPath =
      user.pairedAt.trim() !== ''
        ? getPostAuthHomePath(user)
        : '/pairing/status';
    const timer = window.setTimeout(() => {
      navigate(targetPath, { replace: true });
    }, LANDING_REDIRECT_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isAwaitingAuthRedirect, navigate, user]);

  const dashboardPath =
    user && isAuthenticated ? getPostAuthHomePath(user) : '/login';
  const showLandingHero = !isAuthenticated || isAwaitingAuthRedirect;

  return (
    <div className={`relative min-h-screen overflow-hidden bg-gray-50 dark:bg-gray-950 ${PLAY_BANNER_OFFSET_PADDING_CLASS}`}>
      {showLandingHero ? (
        <div
          className="home-auth-animated-gradient absolute inset-0"
          aria-hidden
        />
      ) : null}
      <div className={`absolute right-4 z-10 flex items-center gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
        {!isAuthenticated ? <LanguageSwitcher /> : null}
        {isAuthenticated && !isAwaitingAuthRedirect ? (
          <>
            <MainMenuButton />
            <NotificationBell />
          </>
        ) : null}
      </div>
      <div
        className={`relative z-0 mx-auto flex min-h-screen w-full max-w-[28rem] flex-col items-center justify-between px-6 pt-24 pb-8 transition-opacity duration-700 ease-out ${isPageVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        {showLandingHero ? (
          <div className="flex w-full flex-1 flex-col items-center justify-center text-center">
            <img
              src={APP_ICONS.any512}
              alt="MaidEasy logo"
              className="mx-auto mb-6 h-24 w-24 rounded-[1.75rem] object-contain shadow-sm dark:shadow-black/35"
            />
            <h1
              className="text-[3.25rem] leading-none font-bold tracking-[-0.02em] text-[#0F1633] dark:text-gray-100"
              role="heading"
              aria-level={1}
            >
              {APP_NAME}
            </h1>
            <p className="mt-5 px-4 text-[1.05rem] leading-relaxed text-gray-500 dark:text-gray-300">{t('common.appTagline')}</p>
            {!isAuthenticated ? (
              <p className="mt-16 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[0.95rem] text-gray-400 dark:text-gray-400">
                <Link to="/privacy" className={LANDING_LEGAL_LINK_CLASS}>
                  {t('home.linkPrivacy')}
                </Link>
                <span aria-hidden className="text-gray-300 dark:text-gray-600">
                  ·
                </span>
                <Link to="/terms" className={LANDING_LEGAL_LINK_CLASS}>
                  {t('home.linkTerms')}
                </Link>
                <span aria-hidden className="text-gray-300 dark:text-gray-600">
                  ·
                </span>
                <Link to="/pics" className={LANDING_LEGAL_LINK_CLASS}>
                  {t('home.linkPics')}
                </Link>
                <span aria-hidden className="text-gray-300 dark:text-gray-600">
                  ·
                </span>
                <Link to="/faq" className={LANDING_LEGAL_LINK_CLASS}>
                  {t('home.linkFaq')}
                </Link>
                <span aria-hidden className="text-gray-300 dark:text-gray-600">
                  ·
                </span>
                <a
                  href={FEATURES_INTRO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={0}
                  aria-label={t('home.linkFeaturesAria')}
                  className={LANDING_LEGAL_LINK_CLASS}
                >
                  {t('home.linkFeatures')}
                </a>
              </p>
            ) : null}
            {isLoading || isAwaitingAuthRedirect ? (
              <div
                className="mt-16 flex justify-center"
                role="status"
                aria-label={t('common.loading')}
              >
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
              </div>
            ) : null}
          </div>
        ) : null}

        <nav
          className={`w-full space-y-4 ${!isLoading && !isAuthenticated ? 'mt-8 pb-14' : 'pb-2'}`}
          aria-label={t('common.appTagline')}
        >
          {!isLoading && !isAuthenticated ? (
            <AuthLandingNavButtons
              onEmailLogin={() => navigate('/login')}
              onGoogleLogin={handleGoogleLogin}
              onRegister={() => navigate('/register')}
              googleBusy={googleBusy}
            />
          ) : null}
          {!isLoading && isAuthenticated && user && !isAwaitingAuthRedirect ? (
            <>
              <Link
                to={dashboardPath}
                className={appButtonClassName({
                  variant: 'primary',
                  className: 'min-w-[8rem] shadow-sm',
                })}
              >
                {user.role === USER_ROLE.EMPLOYER
                  ? t('home.navEmployerApp')
                  : t('home.navHelperApp')}
              </Link>
              <Link
                to="/settings"
                className={appButtonClassName({
                  variant: 'secondary',
                  className: 'min-w-[8rem] shadow-sm',
                })}
              >
                {t('settings.menuSettings')}
              </Link>
              <Link
                to="/pairing/status"
                className={appButtonClassName({
                  variant: 'secondary',
                  className: 'min-w-[8rem] shadow-sm',
                })}
              >
                {t('home.navPairing')}
              </Link>
              <button
                type="button"
                onClick={handleOpenLogoutConfirm}
                className="inline-flex min-h-11 min-w-[8rem] cursor-pointer items-center justify-center rounded-xl border border-red-200 bg-white px-5 py-2 font-semibold text-red-700 shadow-sm transition hover:bg-red-50"
                aria-label={t('home.navLogout')}
              >
                {t('home.navLogout')}
              </button>
            </>
          ) : null}
        </nav>
        {!isAuthenticated && googleError ? (
          <p className="mt-3 w-full max-w-sm rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {googleError}
          </p>
        ) : null}
      </div>
      <ConfirmDialog
        open={logoutConfirmOpen}
        title={t('settings.logoutConfirmTitle')}
        description={t('settings.logoutConfirmDescription')}
        confirmLabel={t('settings.logout')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmLogout()}
        onCancel={handleCloseLogoutConfirm}
        variant="danger"
      />
    </div>
  );
};
